import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import IrisToast from './IrisToast';
import soundService from '@/services/sounds';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
  actionLabel?: string;
  action?: () => void;
  duration?: number;
}

interface ToastContextValue {
  show: (
    message: string,
    type?: ToastType,
    options?: { actionLabel?: string; action?: () => void; duration?: number },
  ) => void;
  success: (
    message: string,
    options?: { actionLabel?: string; action?: () => void; duration?: number },
  ) => void;
  error: (
    message: string,
    options?: { actionLabel?: string; action?: () => void; duration?: number },
  ) => void;
  info: (
    message: string,
    options?: { actionLabel?: string; action?: () => void; duration?: number },
  ) => void;
  warning: (
    message: string,
    options?: { actionLabel?: string; action?: () => void; duration?: number },
  ) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};

export default function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => String(t.id) !== String(id)));
  }, []);

  const show = useCallback(
    (
      message: string,
      type: ToastType = 'info',
      options?: { actionLabel?: string; action?: () => void; duration?: number },
    ) => {
      const id = Date.now() + Math.random(); // Generate unique ID using timestamp + random
      setToasts((prev) => [
        ...prev,
        {
          id,
          type,
          message,
          actionLabel: options?.actionLabel,
          action: options?.action,
          duration: options?.duration,
        },
      ]);

      // Play sound based on toast type
      if (type === 'error') {
        soundService.play('notification'); // Error notification sound
      } else if (type === 'warning') {
        soundService.play('notification'); // Warning notification sound
      } else if (type === 'success') {
        soundService.play('ding'); // Success sound
      } else {
        soundService.play('notification'); // Info notification sound
      }

      const dismissAfter = options?.duration ?? 3000;
      setTimeout(() => remove(id), dismissAfter);
    },
    [remove],
  );

  const success = useCallback(
    (message: string, options?: { actionLabel?: string; action?: () => void; duration?: number }) =>
      show(message, 'success', options),
    [show],
  );
  const error = useCallback(
    (message: string, options?: { actionLabel?: string; action?: () => void; duration?: number }) =>
      show(message, 'error', options),
    [show],
  );
  const info = useCallback(
    (message: string, options?: { actionLabel?: string; action?: () => void; duration?: number }) =>
      show(message, 'info', options),
    [show],
  );
  const warning = useCallback(
    (message: string, options?: { actionLabel?: string; action?: () => void; duration?: number }) =>
      show(message, 'warning', options),
    [show],
  );

  useEffect(() => {
    // escape closes latest toast
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const last = toasts[toasts.length - 1];
        if (last) remove(last.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toasts, remove]);

  // Listen for permission denied events from API interceptor
  useEffect(() => {
    const handlePermissionDenied = (event: CustomEvent) => {
      error(event.detail.message);
    };
    window.addEventListener('permission-denied', handlePermissionDenied as EventListener);
    return () =>
      window.removeEventListener('permission-denied', handlePermissionDenied as EventListener);
  }, [error]);

  return (
    <ToastContext.Provider value={{ show, success, error, info, warning }}>
      {children}
      <div className="fixed bottom-4 right-4 space-y-3 z-50">
        {toasts.map((t) => (
          <IrisToast
            key={t.id}
            id={t.id}
            type={t.type}
            message={t.message}
            actionLabel={t.actionLabel}
            onAction={t.action}
            duration={t.duration}
            onClose={() => remove(t.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
